import React, { useEffect, useState } from "react";
import { Button, Table, Spinner } from "react-bootstrap";
import { toast } from "react-toastify";
import PropTypes from "prop-types";

import { getJobListing } from "../../connections/getJobListing";
import { getJobRequestDetails } from "../../connections/getJobRequestDetails";

const JobStatusExportTable = ({ token, visible }) => {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(false);

  // per-row loading for download
  const [downloadingJobId, setDownloadingJobId] = useState(null);

  useEffect(() => {
    if (visible) fetchJobs();
  }, [visible]);

  const fetchJobs = async () => {
    try {
      setLoading(true);

      const res = await getJobListing(token, "invoice_items_with_purchase");
      const jobRequests = res?.payload?.job_requests || [];
      setJobs(jobRequests);
    } catch (error) {
      toast.error("Failed to fetch job listings");
    } finally {
      setLoading(false);
    }
  };

  const downloadFromUrl = (url, filename) => {
    const a = document.createElement("a");
    a.href = url;
    a.target = "_blank";
    a.rel = "noopener noreferrer";
    if (filename) a.download = filename; // may be ignored for cross-origin
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const handleDownload = async (job) => {
    try {
      setDownloadingJobId(job.id);

      const detailsRes = await getJobRequestDetails(token, job.id);
      const attachment = detailsRes?.payload?.attachment;

      const url = attachment?.url;
      const filename = attachment?.filename;

      if (!url) {
        toast.error("Attachment URL not found");
        return;
      }

      downloadFromUrl(url, filename);
    } catch (error) {
      console.error("Download error:", error);
      toast.error("Failed to download file");
    } finally {
      setDownloadingJobId(null);
    }
  };

  if (!visible) return null;

  return (
    <div className="card shadow-sm border-0 mt-4">
      <div className="card-body">
        <div className="d-flex align-items-center justify-content-between mb-3">
          <div>
            <h5 className="mb-1 fw-semibold">Job Status</h5>
            <small className="text-muted">
              Check progress and download when status is done.
            </small>
          </div>

          <Button
            variant="outline-secondary"
            onClick={fetchJobs}
            disabled={loading}
          >
            {loading ? (
              <>
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                />{" "}
                Refreshing...
              </>
            ) : (
              "Refresh"
            )}
          </Button>
        </div>

        {loading ? (
          <p className="mb-0">Loading job data...</p>
        ) : jobs?.length > 0 ? (
          <Table striped bordered hover responsive className="mb-0">
            <thead>
              <tr>
                <th>Request Type</th>
                <th>Job ID</th>
                <th>Status</th>

                <th>Start Date & Time </th>
                <th>End Date & Time </th>
                <th style={{ width: 180 }}>Action</th>
              </tr>
            </thead>

            <tbody>
              {jobs?.map((job) => (
                <tr key={job.id}>
                  <td>{job.request_type?.trim() ? job.request_type : "-"}</td>
                  <td>{job.job_id || "-"}</td>
                  <td>{job.status || "-"}</td>
                  <td>{job.start_date || "-"}</td>
                  <td>{job.end_date || "-"}</td>

                  <td>
                    {job.status === "done" ? (
                      <Button
                        variant="success"
                        onClick={() => handleDownload(job)}
                        disabled={downloadingJobId === job.id}
                      >
                        {downloadingJobId === job.id ? (
                          <>
                            <Spinner
                              as="span"
                              animation="border"
                              size="sm"
                              role="status"
                              aria-hidden="true"
                            />{" "}
                            Downloading...
                          </>
                        ) : (
                          "Download"
                        )}
                      </Button>
                    ) : (
                      <Button variant="secondary" disabled>
                        Not Ready
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        ) : (
          <p className="mb-0">No job records found.</p>
        )}
      </div>
    </div>
  );
};

JobStatusExportTable.propTypes = {
  token: PropTypes.string.isRequired,
  visible: PropTypes.bool.isRequired,
};

export default JobStatusExportTable;
